import React, { useState, useRef, useEffect } from "react";
import { useFormik } from "formik";
import {
  Button,
  Form,
  Input,
  Label,
  Select,
  Textarea,
  ErrorText,
} from "../../../styledComponent";
import styles from "./style.module.scss";
import { xpressCardlockApplicationInitialValues } from "../../../../utils/formik/initialvalue";
import { xpressCardlockApplicationValidationSchema } from "../../../../utils/formik/validationSchema";
// import { normalizeInput } from "../../../../utils/helpers";
import { BASE_URL, POST_API_URLS } from "../../../../utils/apiConstant";
import axios from "axios";
import { toast } from "react-toastify";
import { UploadFile } from "../../../ui";
import { SectionTitle } from "../../../styledComponent";
import XpressTermsAndConditionsModal from "../../popups/xpressTermsAndConditionsModal";

const PREFERRED_CARDLOCK_LOCATIONS = [
  {
    value: "DEROCHE",
    label: "Deroche (413 50 Lougheed Highway, Deroche, BC)",
  },
  {
    value: "LAC_LA_HACHE",
    label: "Lac La Hache (3727 Highway 97, Lac La Hache, BC)",
  },
  {
    value: "LOON_RIVER",
    label: "Loon River (Loon River Industrial Park, AB-88, Red Earth Creek, AB)",
  },
  {
    value: "PRINCETON",
    label: "Princeton (580 BC-3, Princeton, BC)",
  },
];

const XpressCardlockApplicationForm = (props) => {
  const [contactPhone, setContactPhone] = useState("");
  const [showTermsModal, setShowTermsModal] = useState(false);
  const formikRef = useRef(null);

  const formik = useFormik({
    initialValues: xpressCardlockApplicationInitialValues,
    validationSchema: xpressCardlockApplicationValidationSchema,
    onSubmit: (values) => formSubmitHandler(values),
  });

  useEffect(() => {
    formikRef.current = formik;
  }, [formik]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (formikRef.current) {
        formikRef.current.setFieldValue("contact_person_phone", contactPhone);
      }
    }, 1000);
    return () => clearTimeout(timer);
  }, [contactPhone]);

  // Check if Cardlock is selected
  const isCardlockSelected =
    formik.values.card_applying_for === "canco_cardlock" ||
    formik.values.card_applying_for === "both";

  const cardApplyingFor = formik.values.card_applying_for;
  const preferredCardlockLocation = formik.values.preferred_cardlock_location;
  const { setFieldValue } = formik;

  useEffect(() => {
    const isCardlock =
      cardApplyingFor === "canco_cardlock" || cardApplyingFor === "both";
    if (!isCardlock && preferredCardlockLocation) {
      setFieldValue("preferred_cardlock_location", "");
    }
  }, [cardApplyingFor, preferredCardlockLocation, setFieldValue]);

  // Transform form data to FormData for multipart/form-data submission
  const transformFormData = (data) => {
    // Map card_applying_for to card_type (CardTypeEnum: XPRESS, CARDLOCK, BOTH)
    let cardType = "";
    if (data.card_applying_for === "canco_xpress_fleet") {
      cardType = "XPRESS";
    } else if (data.card_applying_for === "canco_cardlock") {
      cardType = "CARDLOCK";
    } else if (data.card_applying_for === "both") {
      cardType = "BOTH";
    }

    // Map customer_type (CustomerTypeEnum: TRANSPORTATION, CONSTRUCTION, AGRICULTURE, OTHER)
    const customerTypeMap = {
      transportation: "TRANSPORTATION",
      construction: "CONSTRUCTION",
      agriculture: "AGRICULTURE",
      others: "OTHER",
    };

    // Map hear_about_canco (HearAboutEnum: SEARCH_ENGINE, SOCIAL_MEDIA, REFERRAL, OTHER)
    const hearAboutMap = {
      search_engine: "SEARCH_ENGINE",
      social_media: "SOCIAL_MEDIA",
      referral: "REFERRAL",
      others: "OTHER",
    };

    // Map business_structure (BusinessStructureEnum: SOLE, PARTNERSHIP, INCORPORATED, GOVERNMENT, NON_PROFIT)
    const businessStructureMap = {
      sole_proprietorship: "SOLE",
      partnership: "PARTNERSHIP",
      incorporated: "INCORPORATED",
      government_entity: "GOVERNMENT",
      non_profit_organization: "NON_PROFIT",
    };

    // Determine is_gst_exempt based on whether gst_tax_exempt_number is provided
    const isGstExempt = !!data.gst_tax_exempt_number;

    // Create FormData object for multipart/form-data submission
    const formData = new FormData();

    // Append all fields to FormData
    formData.append("card_type", cardType);
    formData.append("customer_type", customerTypeMap[data.customer_type] || "");
    if (data.customer_type_other) {
      formData.append("customer_other_text", data.customer_type_other);
    }
    formData.append("hear_about", hearAboutMap[data.hear_about_canco] || "");
    if (data.hear_about_canco_other) {
      formData.append("hear_about_text", data.hear_about_canco_other);
    }

    // Append files as File objects (not Base64)
    if (data.financial_statement instanceof File) {
      formData.append("financial_statement", data.financial_statement);
    }

    formData.append("complete_legal_business_name", data.legal_business_name || "");
    if (data.trade_name_dba) {
      formData.append("trade_name_dba", data.trade_name_dba);
    }
    formData.append("business_structure", businessStructureMap[data.business_structure] || "");
    formData.append("years_in_business", data.years_in_business || "");
    formData.append("business_number", data.business_number || "");
    formData.append("is_gst_exempt", isGstExempt);

    // gst_document is not in form, skip if not provided
    // If backend requires it, we can append an empty file or skip

    if (data.gst_tax_exempt_number) {
      formData.append("gst_number", data.gst_tax_exempt_number);
    }
    formData.append("business_mailing_address", data.business_mailing_address || "");
    formData.append("contact_full_name", data.contact_person_full_name || "");
    formData.append("contact_title", data.contact_person_title || "");
    formData.append("contact_email", data.contact_person_email || "");
    formData.append("contact_phone", data.contact_person_phone || "");
    formData.append("fleet_size", parseInt(data.fleet_size) || 0);
    formData.append("total_volume_per_month", data.total_anticipated_volume || "");
    formData.append("number_of_cards", parseInt(data.number_of_cards) || 0);
    formData.append("avg_transaction_per_card", data.average_transaction || "");
    formData.append(
      "avg_transactions_per_day",
      data.average_transactions_per_day || ""
    );
    formData.append("bank_name", data.banking_institution_name || "");
    formData.append("transit_number", data.transit_number || "");
    formData.append("institution_number", data.institution_number || "");
    formData.append("account_number", data.account_number || "");

    // Append void_cheque as File object
    if (data.void_cheque instanceof File) {
      formData.append("void_cheque", data.void_cheque);
    }

    if (
      data.card_applying_for === "canco_cardlock" ||
      data.card_applying_for === "both"
    ) {
      formData.append(
        "preferred_cardlock_location",
        data.preferred_cardlock_location || ""
      );
    }

    formData.append("acknowledgement", data.acknowledgement || false);
    formData.append("price_notification_opt_in", data.price_notifications || false);
    if (data.price_notification_email) {
      formData.append("price_notification_email", data.price_notification_email);
    }

    return formData;
  };

  async function formSubmitHandler(data) {
    props.loader(true);
    try {
      const formData = transformFormData(data);
      const apiResponse = await axios.post(
        `${BASE_URL + POST_API_URLS.XPRESS_CARDLOCK_APPLICATION}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      props.loader(false);
      apiResponse && props.success(true);
    } catch (error) {
      console.log("error", error?.response);
      props.loader(false);
      toast.error(
        error?.response?.data?.message || "Error while submitting Form",
        {
          position: "top-right",
          autoClose: 1500,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: false,
          draggable: false,
          progress: undefined,
          theme: "colored",
          style: { fontSize: "1.6rem", fontWeight: 500 },
        }
      );
    }
  }

  return (
    <Form onSubmit={formik.handleSubmit} className={styles.xpressCardlockForm}>
      {/* Section 1 - Business Information */}
      <div className={styles.section}>
        <SectionTitle $size="2.8rem">Section 1 – Business Information</SectionTitle>

        {/* Which card are you applying for? */}
        <div className={styles.fieldGroup}>
          <Label htmlFor="card_applying_for">
            Which card are you applying for?*
          </Label>
          <div className={styles.radioGroup}>
            <label className={styles.radioLabel}>
              <input
                type="radio"
                name="card_applying_for"
                value="canco_xpress_fleet"
                checked={formik.values.card_applying_for === "canco_xpress_fleet"}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              <span>Canco Xpress Fleet</span>
            </label>
            <label className={styles.radioLabel}>
              <input
                type="radio"
                name="card_applying_for"
                value="canco_cardlock"
                checked={formik.values.card_applying_for === "canco_cardlock"}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              <span>Canco Connect Cardlock</span>
            </label>
            <label className={styles.radioLabel}>
              <input
                type="radio"
                name="card_applying_for"
                value="both"
                checked={formik.values.card_applying_for === "both"}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              <span>Both Canco Xpress Fleet and Canco Connect Cardlock</span>
            </label>
          </div>
          {formik.touched.card_applying_for && formik.errors.card_applying_for && (
            <ErrorText className={styles.errorText}>
              {formik.errors.card_applying_for}
            </ErrorText>
          )}
        </div>

        {/* Preferred Canco Connect Cardlock Location */}
        {isCardlockSelected && (
          <div className={styles.fieldGroup}>
            <Label htmlFor="preferred_cardlock_location">
              Preferred Canco Connect Cardlock Location*
            </Label>
            <Select
              id="preferred_cardlock_location"
              {...formik.getFieldProps("preferred_cardlock_location")}
              style={
                formik.touched.preferred_cardlock_location &&
                  formik.errors.preferred_cardlock_location
                  ? { boxShadow: "0px 0px 0px 2px red" }
                  : {}
              }
            >
              <option value="">Select</option>
              {PREFERRED_CARDLOCK_LOCATIONS.map((location) => (
                <option key={location.value} value={location.value}>
                  {location.label}
                </option>
              ))}
            </Select>
            {formik.touched.preferred_cardlock_location &&
              formik.errors.preferred_cardlock_location && (
                <ErrorText className={styles.errorText}>
                  {formik.errors.preferred_cardlock_location}
                </ErrorText>
              )}
          </div>
        )}

        {/* Customer Type and How did you hear about Canco in 2 columns */}
        <div className={styles.fieldsRow}>
          <div className={styles.fieldGroup}>
            <Label htmlFor="customer_type">Customer Type / Industry Category*</Label>
            <Select
              id="customer_type"
              {...formik.getFieldProps("customer_type")}
              style={
                formik.touched.customer_type && formik.errors.customer_type
                  ? { boxShadow: "0px 0px 0px 2px red" }
                  : {}
              }
            >
              <option value="">Select</option>
              <option value="transportation">Transportation</option>
              <option value="construction">Construction</option>
              <option value="agriculture">Agriculture</option>
              <option value="others">Others (Please specify)</option>
            </Select>
            {formik.touched.customer_type && formik.errors.customer_type && (
              <ErrorText className={styles.errorText}>
                {formik.errors.customer_type}
              </ErrorText>
            )}
            {formik.values.customer_type === "others" && (
              <div className={styles.conditionalField}>
                <Input
                  type="text"
                  placeholder="Please specify"
                  {...formik.getFieldProps("customer_type_other")}
                  style={
                    formik.touched.customer_type_other && formik.errors.customer_type_other
                      ? { boxShadow: "0px 0px 0px 2px red" }
                      : {}
                  }
                />
                {formik.touched.customer_type_other && formik.errors.customer_type_other && (
                  <ErrorText className={styles.errorText}>
                    {formik.errors.customer_type_other}
                  </ErrorText>
                )}
              </div>
            )}
          </div>

          <div className={styles.fieldGroup}>
            <Label htmlFor="hear_about_canco">How did you hear about Canco?*</Label>
            <Select
              id="hear_about_canco"
              {...formik.getFieldProps("hear_about_canco")}
              style={
                formik.touched.hear_about_canco && formik.errors.hear_about_canco
                  ? { boxShadow: "0px 0px 0px 2px red" }
                  : {}
              }
            >
              <option value="">Select</option>
              <option value="search_engine">Search Engine (Google, Bing, etc.)</option>
              <option value="social_media">Social Media (online ads)</option>
              <option value="referral">Referral</option>
              <option value="others">Others (Please specify)</option>
            </Select>
            {formik.touched.hear_about_canco && formik.errors.hear_about_canco && (
              <ErrorText className={styles.errorText}>
                {formik.errors.hear_about_canco}
              </ErrorText>
            )}
            {(formik.values.hear_about_canco === "referral" ||
              formik.values.hear_about_canco === "others") && (
                <div className={styles.conditionalField}>
                  <Input
                    type="text"
                    placeholder="Please specify"
                    {...formik.getFieldProps("hear_about_canco_other")}
                    style={
                      formik.touched.hear_about_canco_other &&
                        formik.errors.hear_about_canco_other
                        ? { boxShadow: "0px 0px 0px 2px red" }
                        : {}
                    }
                  />
                  {formik.touched.hear_about_canco_other &&
                    formik.errors.hear_about_canco_other && (
                      <ErrorText className={styles.errorText}>
                        {formik.errors.hear_about_canco_other}
                      </ErrorText>
                    )}
                </div>
              )}
          </div>
        </div>

        {/* Upload Financial Statement */}
        <div className={styles.fieldGroup}>
          <Label htmlFor="financial_statement">
            Upload Financial Statement (Optional)
          </Label>
          <p className={styles.fieldNote}>
            Required only for applications requesting over $15,000 credit.
          </p>
          <UploadFile
            onChange={(e) =>
              formik.setFieldValue(
                "financial_statement",
                e.currentTarget.files[0]
              )
            }
            onBlur={formik.handleBlur("financial_statement")}
            uploadName="financial_statement"
            placeholder="Upload Financial Statement (Optional)"
          />
        </div>

        {/* Complete Legal Business Name and Business Structure in 2 columns */}
        <div className={styles.fieldsRow}>
          <div className={styles.fieldGroup}>
            <Label htmlFor="legal_business_name">Complete Legal Business Name*</Label>
            <Input
              type="text"
              id="legal_business_name"
              {...formik.getFieldProps("legal_business_name")}
              style={
                formik.touched.legal_business_name && formik.errors.legal_business_name
                  ? { boxShadow: "0px 0px 0px 2px red" }
                  : {}
              }
            />
            {formik.touched.legal_business_name && formik.errors.legal_business_name && (
              <ErrorText className={styles.errorText}>
                {formik.errors.legal_business_name}
              </ErrorText>
            )}
          </div>

          <div className={styles.fieldGroup}>
            <Label htmlFor="business_structure">Business Type*</Label>
            <Select
              id="business_structure"
              {...formik.getFieldProps("business_structure")}
              style={
                formik.touched.business_structure && formik.errors.business_structure
                  ? { boxShadow: "0px 0px 0px 2px red" }
                  : {}
              }
            >
              <option value="">Select</option>
              <option value="sole_proprietorship">Sole Proprietorship</option>
              <option value="partnership">Partnership</option>
              <option value="incorporated">Incorporated</option>
              <option value="government_entity">Government Entity</option>
              <option value="non_profit_organization">Non-Profit Organization</option>
            </Select>
            {formik.touched.business_structure && formik.errors.business_structure && (
              <ErrorText className={styles.errorText}>
                {formik.errors.business_structure}
              </ErrorText>
            )}
          </div>
        </div>

        {/* Trade Name / DBA */}
        <div className={styles.fieldGroup}>
          <Label htmlFor="trade_name_dba">
            Trade Name / DBA (Doing Business As), if different from Legal Business Name
          </Label>
          <Input
            type="text"
            id="trade_name_dba"
            {...formik.getFieldProps("trade_name_dba")}
          />
        </div>

        {/* How long has this company been in business? */}
        <div className={styles.fieldGroup}>
          <Label htmlFor="years_in_business">
            How long has this company been in business?*
          </Label>
          <Input
            type="text"
            id="years_in_business"
            {...formik.getFieldProps("years_in_business")}
            style={
              formik.touched.years_in_business && formik.errors.years_in_business
                ? { boxShadow: "0px 0px 0px 2px red" }
                : {}
            }
          />
          {formik.touched.years_in_business && formik.errors.years_in_business && (
            <ErrorText className={styles.errorText}>
              {formik.errors.years_in_business}
            </ErrorText>
          )}
        </div>

        {/* Business Number and GST Tax exempt number in 2 columns */}
        <div className={styles.fieldsRow}>
          {/* Business Number */}
          <div className={styles.fieldGroup}>
            <Label htmlFor="business_number">Business Number*</Label>
            <Input
              type="text"
              id="business_number"
              {...formik.getFieldProps("business_number")}
              style={
                formik.touched.business_number && formik.errors.business_number
                  ? { boxShadow: "0px 0px 0px 2px red" }
                  : {}
              }
            />
            {formik.touched.business_number && formik.errors.business_number && (
              <ErrorText className={styles.errorText}>
                {formik.errors.business_number}
              </ErrorText>
            )}
          </div>

          {/* GST Tax exempt number */}
          <div className={styles.fieldGroup}>
            <Label htmlFor="gst_tax_exempt_number">
              GST Tax exempt number
            </Label>
            <Input
              type="text"
              id="gst_tax_exempt_number"
              {...formik.getFieldProps("gst_tax_exempt_number")}
            />
          </div>
        </div>

        {/* Business Full Mailing Address */}
        <div className={styles.fieldGroup}>
          <Label htmlFor="business_mailing_address">Business Full Mailing Address* (Not a PO BOX)</Label>
          <Textarea
            id="business_mailing_address"
            rows={3}
            {...formik.getFieldProps("business_mailing_address")}
            style={
              formik.touched.business_mailing_address &&
                formik.errors.business_mailing_address
                ? { boxShadow: "0px 0px 0px 2px red" }
                : {}
            }
          />
          {formik.touched.business_mailing_address &&
            formik.errors.business_mailing_address && (
              <ErrorText className={styles.errorText}>
                {formik.errors.business_mailing_address}
              </ErrorText>
            )}
        </div>

        {/* Contact Person Full Name and Title in 2 columns */}
        <div className={styles.fieldsRow}>
          {/* Contact Person Full Name */}
          <div className={styles.fieldGroup}>
            <Label htmlFor="contact_person_full_name">Contact Person Full Name*</Label>
            <Input
              type="text"
              id="contact_person_full_name"
              {...formik.getFieldProps("contact_person_full_name")}
              style={
                formik.touched.contact_person_full_name &&
                  formik.errors.contact_person_full_name
                  ? { boxShadow: "0px 0px 0px 2px red" }
                  : {}
              }
            />
            {formik.touched.contact_person_full_name &&
              formik.errors.contact_person_full_name && (
                <ErrorText className={styles.errorText}>
                  {formik.errors.contact_person_full_name}
                </ErrorText>
              )}
          </div>

          {/* Contact Person Title */}
          <div className={styles.fieldGroup}>
            <Label htmlFor="contact_person_title">Contact Person Title*</Label>
            <Input
              type="text"
              id="contact_person_title"
              {...formik.getFieldProps("contact_person_title")}
              style={
                formik.touched.contact_person_title && formik.errors.contact_person_title
                  ? { boxShadow: "0px 0px 0px 2px red" }
                  : {}
              }
            />
            {formik.touched.contact_person_title && formik.errors.contact_person_title && (
              <ErrorText className={styles.errorText}>
                {formik.errors.contact_person_title}
              </ErrorText>
            )}
          </div>
        </div>

        {/* Contact Person Email and Phone in 2 columns */}
        <div className={styles.fieldsRow}>
          {/* Contact Person Email */}
          <div className={styles.fieldGroup}>
            <Label htmlFor="contact_person_email">Contact Person Email*</Label>
            <Input
              type="email"
              id="contact_person_email"
              {...formik.getFieldProps("contact_person_email")}
              style={
                formik.touched.contact_person_email && formik.errors.contact_person_email
                  ? { boxShadow: "0px 0px 0px 2px red" }
                  : {}
              }
            />
            {formik.touched.contact_person_email && formik.errors.contact_person_email && (
              <ErrorText className={styles.errorText}>
                {formik.errors.contact_person_email}
              </ErrorText>
            )}
          </div>

          {/* Contact Person Phone */}
          <div className={styles.fieldGroup}>
            <Label htmlFor="contact_person_phone">Contact Person Phone*</Label>
            <Input
              type="text"
              name="contact_person_phone"
              id="contact_person_phone"
              placeholder="xxx-xxx-xxxx"
              onBlur={formik.handleBlur("contact_person_phone")}
              onChange={(e) => {
                const value = e.target.value.replace(/[^\d]/g, "");
                if (value.length <= 10) {
                  let formatted = value;
                  if (value.length >= 7) {
                    formatted = `${value.slice(0, 3)}-${value.slice(3, 6)}-${value.slice(6, 10)}`;
                  } else if (value.length >= 4) {
                    formatted = `${value.slice(0, 3)}-${value.slice(3)}`;
                  }
                  setContactPhone(formatted);
                }
              }}
              value={contactPhone}
              style={
                formik.touched.contact_person_phone && formik.errors.contact_person_phone
                  ? { boxShadow: "0px 0px 0px 2px red" }
                  : {}
              }
            />
            {formik.touched.contact_person_phone && formik.errors.contact_person_phone && (
              <ErrorText className={styles.errorText}>
                {formik.errors.contact_person_phone}
              </ErrorText>
            )}
          </div>
        </div>
      </div>

      {/* Section 2 - Fleet Information */}
      <div className={styles.section}>
        <SectionTitle $size="2.8rem">Section 2 – Fleet Information</SectionTitle>

        {/* Size of Fleet */}
        <div className={styles.fieldGroup}>
          <Label htmlFor="fleet_size">Size of Fleet*</Label>
          <Input
            type="text"
            id="fleet_size"
            placeholder="Estimated number of vehicles"
            {...formik.getFieldProps("fleet_size")}
            style={
              formik.touched.fleet_size && formik.errors.fleet_size
                ? { boxShadow: "0px 0px 0px 2px red" }
                : {}
            }
          />
          {formik.touched.fleet_size && formik.errors.fleet_size && (
            <ErrorText className={styles.errorText}>
              {formik.errors.fleet_size}
            </ErrorText>
          )}
        </div>

        {/* Total Anticipated Volume */}
        <div className={styles.fieldGroup}>
          <Label htmlFor="total_anticipated_volume">
            Total Anticipated Volume per Month for Entire Fleet* (in litres)
          </Label>
          <Input
            type="text"
            id="total_anticipated_volume"
            {...formik.getFieldProps("total_anticipated_volume")}
            onInput={(e) => {
              e.target.value = e.target.value.replace(/[^\d]/g, "");
            }}
            style={
              formik.touched.total_anticipated_volume &&
                formik.errors.total_anticipated_volume
                ? { boxShadow: "0px 0px 0px 2px red" }
                : {}
            }
          />
          {formik.touched.total_anticipated_volume &&
            formik.errors.total_anticipated_volume && (
              <ErrorText className={styles.errorText}>
                {formik.errors.total_anticipated_volume}
              </ErrorText>
            )}
        </div>

        {/* Number of Cards Requested */}
        <div className={styles.fieldGroup}>
          <Label htmlFor="number_of_cards">Number of Cards Requested*</Label>
          <Input
            type="text"
            id="number_of_cards"
            {...formik.getFieldProps("number_of_cards")}
            onInput={(e) => {
              e.target.value = e.target.value.replace(/[^\d]/g, "");
            }}
            style={
              formik.touched.number_of_cards && formik.errors.number_of_cards
                ? { boxShadow: "0px 0px 0px 2px red" }
                : {}
            }
          />
          {formik.touched.number_of_cards && formik.errors.number_of_cards && (
            <ErrorText className={styles.errorText}>
              {formik.errors.number_of_cards}
            </ErrorText>
          )}
        </div>

        {/* Average Single Transaction */}
        <div className={styles.fieldGroup}>
          <Label htmlFor="average_transaction">
            Average Single Transaction per Card (in $)*
          </Label>
          <Input
            type="text"
            id="average_transaction"
            {...formik.getFieldProps("average_transaction")}
            onInput={(e) => {
              e.target.value = e.target.value.replace(/[^\d.]/g, "");
            }}
            style={
              formik.touched.average_transaction && formik.errors.average_transaction
                ? { boxShadow: "0px 0px 0px 2px red" }
                : {}
            }
          />
          {formik.touched.average_transaction && formik.errors.average_transaction && (
            <ErrorText className={styles.errorText}>
              {formik.errors.average_transaction}
            </ErrorText>
          )}
        </div>

        {/* Average Number of Transactions per Day */}
        <div className={styles.fieldGroup}>
          <Label htmlFor="average_transactions_per_day">
            Average Number of Transactions per Day (estimate if unsure)*
          </Label>
          <Input
            type="text"
            id="average_transactions_per_day"
            {...formik.getFieldProps("average_transactions_per_day")}
            onInput={(e) => {
              e.target.value = e.target.value.replace(/[^\d]/g, "");
            }}
            style={
              formik.touched.average_transactions_per_day &&
                formik.errors.average_transactions_per_day
                ? { boxShadow: "0px 0px 0px 2px red" }
                : {}
            }
          />
          {formik.touched.average_transactions_per_day &&
            formik.errors.average_transactions_per_day && (
              <ErrorText className={styles.errorText}>
                {formik.errors.average_transactions_per_day}
              </ErrorText>
            )}
        </div>

        {/* Price Notifications */}
        {isCardlockSelected && (
          <div className={styles.fieldGroup}>
            <div className={styles.checkboxGroup}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  id="price_notifications"
                  checked={formik.values.price_notifications || false}
                  onChange={(e) => {
                    formik.setFieldValue("price_notifications", e.target.checked);
                    if (!e.target.checked) {
                      formik.setFieldValue("price_notification_email", "");
                    }
                  }}
                  onBlur={formik.handleBlur("price_notifications")}
                />
                <span className={styles.checkmark}></span>
                <span>Do you want to receive Price Notifications via email?</span>
              </label>
            </div>
            {formik.touched.price_notifications && formik.errors.price_notifications && (
              <ErrorText className={styles.errorText}>
                {formik.errors.price_notifications}
              </ErrorText>
            )}
            {formik.values.price_notifications && (
              <div className={styles.conditionalField}>
                <Label htmlFor="price_notification_email">Email Address*</Label>
                <Input
                  type="email"
                  id="price_notification_email"
                  placeholder="Enter email address"
                  {...formik.getFieldProps("price_notification_email")}
                  style={
                    formik.touched.price_notification_email &&
                      formik.errors.price_notification_email
                      ? { boxShadow: "0px 0px 0px 2px red" }
                      : {}
                  }
                />
                {formik.touched.price_notification_email &&
                  formik.errors.price_notification_email && (
                    <ErrorText className={styles.errorText}>
                      {formik.errors.price_notification_email}
                    </ErrorText>
                  )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Section 3 - Banking Reference */}
      <div className={styles.section}>
        <SectionTitle $size="2.8rem">Section 3 – Banking Reference</SectionTitle>

        {/* Banking Institution Name */}
        <div className={styles.fieldGroup}>
          <Label htmlFor="banking_institution_name">Banking Institution Name*</Label>
          <Input
            type="text"
            id="banking_institution_name"
            {...formik.getFieldProps("banking_institution_name")}
            style={
              formik.touched.banking_institution_name &&
                formik.errors.banking_institution_name
                ? { boxShadow: "0px 0px 0px 2px red" }
                : {}
            }
          />
          {formik.touched.banking_institution_name &&
            formik.errors.banking_institution_name && (
              <ErrorText className={styles.errorText}>
                {formik.errors.banking_institution_name}
              </ErrorText>
            )}
        </div>

        {/* Transit Number and Institution Number in 2 columns */}
        <div className={styles.fieldsRow}>
          {/* Transit Number */}
          <div className={styles.fieldGroup}>
            <Label htmlFor="transit_number">Transit Number*</Label>
            <Input
              type="text"
              id="transit_number"
              {...formik.getFieldProps("transit_number")}
              onInput={(e) => {
                e.target.value = e.target.value.replace(/[^\d]/g, "");
              }}
              style={
                formik.touched.transit_number && formik.errors.transit_number
                  ? { boxShadow: "0px 0px 0px 2px red" }
                  : {}
              }
            />
            {formik.touched.transit_number && formik.errors.transit_number && (
              <ErrorText className={styles.errorText}>
                {formik.errors.transit_number}
              </ErrorText>
            )}
          </div>

          {/* Institution Number */}
          <div className={styles.fieldGroup}>
            <Label htmlFor="institution_number">Institution Number*</Label>
            <Input
              type="text"
              id="institution_number"
              {...formik.getFieldProps("institution_number")}
              onInput={(e) => {
                e.target.value = e.target.value.replace(/[^\d]/g, "");
              }}
              style={
                formik.touched.institution_number && formik.errors.institution_number
                  ? { boxShadow: "0px 0px 0px 2px red" }
                  : {}
              }
            />
            {formik.touched.institution_number && formik.errors.institution_number && (
              <ErrorText className={styles.errorText}>
                {formik.errors.institution_number}
              </ErrorText>
            )}
          </div>
        </div>

        {/* Account Number */}
        <div className={styles.fieldGroup}>
          <Label htmlFor="account_number">Account Number*</Label>
          <Input
            type="text"
            id="account_number"
            {...formik.getFieldProps("account_number")}
            onInput={(e) => {
              e.target.value = e.target.value.replace(/[^\d]/g, "");
            }}
            style={
              formik.touched.account_number && formik.errors.account_number
                ? { boxShadow: "0px 0px 0px 2px red" }
                : {}
            }
          />
          {formik.touched.account_number && formik.errors.account_number && (
            <ErrorText className={styles.errorText}>
              {formik.errors.account_number}
            </ErrorText>
          )}
        </div>

        {/* Upload Void Cheque */}
        <div className={styles.fieldGroup}>
          <Label htmlFor="void_cheque">
            Upload Void Cheque*{" "}
            <span className={styles.fieldDescription}>
              (Required for PAD payment setup.)
            </span>
          </Label>
          <UploadFile
            onChange={(e) =>
              formik.setFieldValue("void_cheque", e.currentTarget.files[0])
            }
            onBlur={formik.handleBlur("void_cheque")}
            uploadName="void_cheque"
          />
          {formik.touched.void_cheque && formik.errors.void_cheque && (
            <ErrorText className={styles.errorText}>
              {formik.errors.void_cheque}
            </ErrorText>
          )}
        </div>
      </div>

      {/* Acknowledgement */}
      <div className={styles.section}>
        <SectionTitle $size="2.8rem">Acknowledgement*</SectionTitle>
        <div className={styles.acknowledgementText}>
          <p>
            By submitting this application, I,{" "}
            <strong>
              {formik.values.contact_person_full_name || "___________"}
            </strong>
            , in my capacity as an authorized representative of{" "}
            <strong>
              {formik.values.legal_business_name || "_________________________"}
            </strong>{" "}
            (the &ldquo;Customer&rdquo;) and on behalf of the Customer, hereby confirm that
            effective as of and from the date that this Xpress Application Form is submitted to
            Canco Petroleum Ltd. (&ldquo;Canco&rdquo;):
          </p>
          <ol className={styles.acknowledgementList}>
            <li>
              the information provided in this Xpress Application Form is accurate to the best
              of my knowledge;
            </li>
            <li>
              the Customer agrees and acknowledges that Canco may conduct one or more credit
              checks on the Customer, the Customer&rsquo;s business, or the Customer&rsquo;s
              listed owners or directors, as part of the evaluation process for any Xpress Fleet
              Card in any way related to the Customer;
            </li>
            <li>
              if I am listed as an owner or guarantor, I acknowledge that I may be personally
              guaranteeing the repayment of any balances, fees, or penalties associated with the
              use of any Xpress Fleet Card in any way related to the Customer, should the
              Customer fail to meet its obligations to Canco;
            </li>
            <li>
              the Customer acknowledges that the Xpress Terms and Conditions attached hereto,
              including without limitation all terms, covenants, provisions and obligations
              contained in the Xpress Terms and Conditions attached hereto, applies to any and
              all Xpress Fleet Cards issued by Canco in any way related to the Customer upon
              Canco&rsquo;s issuance of any and all Xpress Fleet Cards in any way related to the
              Customer;
            </li>
            <li>
              the Customer acknowledges, agrees to, and accepts the Xpress Terms and Conditions
              attached hereto;
            </li>
            <li>
              the consents provided in this Xpress Application Form are effective as of the date
              this Xpress Application Form is submitted to Canco and will be valid for as long as
              required to fulfill the purposes described in this Xpress Application Form and the
              Xpress Terms and Conditions attached hereto; and
            </li>
            <li>
              all capitalized terms and expressions used in this Xpress Application Form not
              defined herein shall have the same meaning as in the Xpress Terms and Conditions
              attached hereto.
            </li>
          </ol>
        </div>
        <div className={styles.acknowledgementCheckbox}>
          <label className={styles.acknowledgementCheckboxLabel}>
            <input
              type="checkbox"
              checked={formik.values.acknowledgement || false}
              onChange={(e) => {
                if (!e.target.checked) {
                  formik.setFieldValue("acknowledgement", false);
                }
              }}
              onClick={(e) => {
                if (!formik.values.acknowledgement) {
                  e.preventDefault();
                  setShowTermsModal(true);
                }
              }}
              onBlur={formik.handleBlur("acknowledgement")}
            />
            <span className={styles.acknowledgementCheckmark}></span>
            <span>I Accept the Terms &amp; Conditions</span>
          </label>
          {formik.touched.acknowledgement && formik.errors.acknowledgement && (
            <ErrorText className={styles.errorText}>
              {formik.errors.acknowledgement}
            </ErrorText>
          )}
        </div>
      </div>

      <XpressTermsAndConditionsModal
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
        onAccept={() => {
          formik.setFieldValue("acknowledgement", true);
          formik.setFieldTouched("acknowledgement", true, false);
        }}
      />

      <Button
        $width="100%"
        type="submit"
        className={
          !(formik.isValid && formik.dirty) ? "disabledBtn" : "submitBtn"
        }
      >
        Submit
      </Button>
    </Form>
  );
};

export default XpressCardlockApplicationForm;

